import { Lock, Fingerprint, Shield, Key, Wifi } from "lucide-react";

/**
 * HeroIllustration — CSS-only abstract vault composition.
 * Floating glass cards layered around a central locked vault icon.
 */
export default function HeroIllustration() {
  return (
    <div
      className="relative w-full aspect-[5/4] max-w-xl mx-auto"
      data-testid="hero-illustration"
    >
      {/* Background glows */}
      <div className="absolute inset-0 rounded-[2rem] mint-glow blur-2xl scale-110" />
      <div className="absolute -top-10 -right-10 h-56 w-56 rounded-full bg-mint/40 blur-3xl" />
      <div className="absolute -bottom-12 -left-10 h-72 w-72 rounded-full bg-mint-300/30 blur-3xl" />

      {/* Outer frame */}
      <div className="relative h-full w-full rounded-[2rem] glass-light p-6 sm:p-8 overflow-hidden grain-overlay">
        {/* grid behind */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] dark:opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />

        {/* Center vault */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-[1.75rem] bg-mint blur-xl opacity-70 animate-pulse-mint" />
            <div className="relative h-32 w-32 sm:h-40 sm:w-40 rounded-[1.75rem] bg-gradient-to-br from-white to-mint-100 dark:from-slate-800 dark:to-slate-900 border border-white/60 dark:border-white/10 shadow-2xl flex items-center justify-center">
              <Lock className="h-12 w-12 sm:h-16 sm:w-16 text-ink dark:text-mint" strokeWidth={1.6} />
            </div>
          </div>
        </div>

        {/* Floating glass cards */}
        <div
          className="absolute top-6 left-6 sm:top-8 sm:left-8 glass-light rounded-2xl px-3.5 py-2.5 flex items-center gap-2.5 animate-float"
          style={{ animationDelay: "0.2s" }}
        >
          <div className="h-8 w-8 rounded-lg bg-mint flex items-center justify-center">
            <Fingerprint className="h-4 w-4 text-ink" />
          </div>
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Biometric
            </p>
            <p className="text-xs font-semibold">Touch unlock</p>
          </div>
        </div>

        <div
          className="absolute bottom-6 left-4 sm:bottom-8 sm:left-6 glass-light rounded-2xl px-3.5 py-2.5 flex items-center gap-2.5 animate-float-slow"
          style={{ animationDelay: "0.6s" }}
        >
          <div className="h-8 w-8 rounded-lg bg-foreground/5 flex items-center justify-center">
            <Wifi className="h-4 w-4 rotate-45 text-destructive" />
          </div>
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Network
            </p>
            <p className="text-xs font-semibold">Offline</p>
          </div>
        </div>

        <div
          className="absolute top-6 right-4 sm:top-10 sm:right-6 glass-light rounded-2xl px-3.5 py-2.5 flex items-center gap-2.5 animate-float"
          style={{ animationDelay: "0.4s" }}
        >
          <div className="h-8 w-8 rounded-lg bg-mint flex items-center justify-center">
            <Shield className="h-4 w-4 text-ink" />
          </div>
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Encryption
            </p>
            <p className="text-xs font-semibold">AES-256-GCM</p>
          </div>
        </div>

        <div
          className="absolute bottom-8 right-6 sm:bottom-10 sm:right-10 glass-light rounded-2xl px-3.5 py-2.5 animate-float-slow"
          style={{ animationDelay: "0.8s" }}
        >
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Master password
          </p>
          <p className="font-mono text-xs mt-1 tracking-wider">
            ●●●●●●●●●●●●●
          </p>
        </div>

        {/* small key chip */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-3 sm:bottom-4 glass-light rounded-full px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-medium">
          <Key className="h-3 w-3" />
          Zero-knowledge · derived locally
        </div>
      </div>
    </div>
  );
}
